import { useState } from "react";
import { css } from "@emotion/react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import type { GridFilterItem } from "@mui/x-data-grid-pro";
import { formatDateTimeInZone } from "@exonn/utils";
import type { ExactTheme } from "src/lib/emotion/theme.ts";
import { Panel } from "src/components/Panel/Panel.tsx";
import { WithHint } from "src/admin/exonn-common/components/HelpHint.tsx";
import { Flex } from "src/components/Flex.tsx";
import { Button } from "src/components/Button/Button.tsx";
import { LinkButton } from "src/components/LinkButton/LinkButton.tsx";
import { TextV2 } from "src/components/TextV2/TextV2.tsx";
import { ResourceEnum } from "src/constants/constants.ts";
import { TranslationNamespaceEnum } from "src/lib/i18next/types.ts";
import { useList } from "src/system/useList.tsx";
import { useErpTranslation } from "src/system/useErpTranslation.ts";
import { sourceLabel } from "src/admin/modules/epost-letter/source.ts";
import { createModuleRoutePaths } from "src/system/createModuleRoutePaths.ts";
import { useOpenRecord } from "src/admin/recommendation/hooks/useOpenRecord.ts";
import { StatusPill } from "src/admin/exonn-common/components/StatusPill.tsx";
import { letterTone } from "src/admin/exonn-epost/epostFormat.ts";
import { formatCents } from "src/admin/exonn-common/format.ts";
import {
  chargedCents,
  type EpostLetterResource,
} from "src/admin/modules/epost-letter/types.ts";

const SHOWN = 5;

// «Alle anzeigen» — все письма этой связи прямо в панели: журнал фильтровать по ней не умеет.
const ALL = 100;

const routePaths = createModuleRoutePaths({
  resource: ResourceEnum.EpostLetter,
});

/**
 * Письма по почте у документа, заказа или клиента (макет 6): статус,
 * когда, цена; «Brief senden» открывает мастер с этим клиентом или
 * документом. Связь настоящая — по документам письма, а не по клиенту.
 */
export const LettersPanel = ({
  filter,
  wizard,
}: {
  filter: GridFilterItem[];
  wizard: {
    customer_id?: string | null;
    document_id?: string | null;
    owner_profile_id?: string | null;
  };
}) => {
  const { t } = useTranslation(TranslationNamespaceEnum.EpostLetters);
  // Подписи источника — как в журнале «Briefe».
  const { t: tList } = useErpTranslation({
    type: "list",
    resource: ResourceEnum.EpostLetter,
  });
  const navigate = useNavigate();
  const openRecord = useOpenRecord();
  const [showAll, setShowAll] = useState(false);
  const { query } = useList<EpostLetterResource>({
    resource: ResourceEnum.EpostLetter,
    filter: { items: filter },
    page: { size: showAll ? ALL : SHOWN, number: 0 },
    useSimplePagination: false,
  });
  const letters = query.data?.data ?? [];
  const total = query.data?.meta?.total ?? letters.length;
  const params = new URLSearchParams(
    Object.entries(wizard).filter(([, value]) => value) as [string, string][],
  );

  return (
    <Panel isLoading={query.isLoading}>
      <Panel.Header>
        <Panel.Title>
          <Panel.Icon>
            <span className={"icon-15-email"} />
          </Panel.Icon>
          <WithHint
            hint={t(`titleHelp.${filter[0]?.field ?? "customer_id"}`, {
              defaultValue: t("titleHelp.customer_id"),
            })}
          >
            {t("title")}
          </WithHint>
        </Panel.Title>
        <Button
          size={"sm"}
          skin={"info"}
          variant={"outlined"}
          css={noShrink}
          onClick={() => navigate(`${routePaths.create.root}?${params}`)}
        >
          {t("send")}
        </Button>
      </Panel.Header>
      <Panel.Content>
        {query.isLoading ? null : letters.length === 0 ? (
          <TextV2 variant={"body1"} css={muted}>
            {t("empty")}
          </TextV2>
        ) : (
          <Flex flexDirection={"column"} gap={"10px"}>
            {letters.map((letter) => {
              const cents = chargedCents(letter);

              return (
                <div key={letter.id} css={row}>
                  {/* Кому и что — у клиента писем много, у документа бывают письма разным людям. */}
                  <span css={what}>
                    {letter.recipient.name} · {sourceLabel(letter, tList)}
                  </span>
                  <Flex alignItems={"center"} gap={"8px"} flexWrap={"wrap"}>
                    <StatusPill
                      tone={letterTone(letter.status)}
                      label={t(`status.${letter.status}`, {
                        defaultValue: letter.status,
                      })}
                    />
                    <span css={muted}>
                      {letter.created_at
                        ? formatDateTimeInZone(letter.created_at)
                        : ""}
                    </span>
                  </Flex>
                  <Flex alignItems={"center"} gap={"12px"} flexWrap={"wrap"}>
                    {/* Как в журнале: «0,00 €» рядом с «nicht berechnet» лишнее. */}
                    {cents === 0 ? (
                      <span css={muted}>{t("notCharged")}</span>
                    ) : (
                      <span css={numeric}>{formatCents(cents)}</span>
                    )}
                    <LinkButton
                      css={tapTarget}
                      onClick={() =>
                        openRecord(ResourceEnum.EpostLetter, letter.id)
                      }
                    >
                      {t("open")}
                    </LinkButton>
                  </Flex>
                </div>
              );
            })}
            {showAll && total > letters.length && (
              <TextV2 variant={"body2"} css={muted}>
                {t("moreInJournal")}
              </TextV2>
            )}
            {!showAll && total > letters.length && (
              <LinkButton css={tapTarget} onClick={() => setShowAll(true)}>
                {t("more", { count: total })}
              </LinkButton>
            )}
          </Flex>
        )}
      </Panel.Content>
    </Panel>
  );
};

const row = (theme: ExactTheme) => css`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 6px 12px;
  padding-bottom: 10px;
  border-bottom: 1px solid ${theme.colorsV2.grey[100]};

  &:last-of-type {
    border-bottom: 0;
    padding-bottom: 0;
  }
`;

const what = css`
  flex-basis: 100%;
  font-size: 14px;
  overflow-wrap: anywhere;
`;

const muted = (theme: ExactTheme) => css`
  color: ${theme.colorsV2.grey[800]};
  font-size: 13px;
`;

const numeric = css`
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  font-size: 13px;
`;

const noShrink = css`
  flex-shrink: 0;
`;

// На телефоне — палец, а не курсор: высота нажатия 44 px.
const tapTarget = css`
  @media (max-width: 600px) {
    min-height: 44px;
    display: inline-flex;
    align-items: center;
  }
`;
